import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, getMemberHeaders } from "../utils/api";
import { Star } from "lucide-react";

export default function TrainerReview() {
  const [searchParams] = useSearchParams();
  const trainerId = searchParams.get("trainer_id") || "";
  const classId   = searchParams.get("class_id") || "";
  const navigate  = useNavigate();

  const [trainerName, setTrainerName] = useState("");
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [notLoggedIn, setNotLoggedIn] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("memberToken")) { setNotLoggedIn(true); return; }
    if (!trainerId) return;
    axios.get(`${API}/trainers`)
      .then(res => {
        const t = res.data.find(t => t.id === trainerId);
        if (t) setTrainerName(t.name);
      })
      .catch(() => {});
  }, [trainerId]);

  const handleSubmit = async () => {
    if (!rating) { toast.error("Please select a star rating"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/member/trainer-review`,
        { trainer_id: trainerId, class_id: classId, rating, comment },
        getMemberHeaders()
      );
      if (res.data.already_submitted) {
        toast.info("You've already reviewed this trainer — thanks!");
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setNotLoggedIn(true);
      } else {
        toast.error(err.response?.data?.detail || "Could not submit review");
      }
    } finally { setLoading(false); }
  };

  if (notLoggedIn) return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="text-center space-y-4 max-w-sm">
        <p className="text-[#FFB612] font-black text-2xl">NBC</p>
        <p className="text-white">Please log in to leave a review</p>
        <button onClick={() => navigate("/login", { state: { redirect: window.location.pathname + window.location.search } })}
          className="w-full bg-[#FFB612] text-black font-black py-3 rounded">Log In</button>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="text-center space-y-4 max-w-sm">
        <div className="text-5xl">🥊</div>
        <h1 className="text-white text-2xl font-black">Thank You!</h1>
        <p className="text-white/50 text-sm">Your anonymous review for {trainerName || "your trainer"} has been submitted. Your feedback helps us keep NBC great.</p>
        <button onClick={() => navigate("/dashboard/classes")}
          className="w-full bg-[#FFB612] text-black font-black py-3 rounded">Back to Classes</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <p className="text-[#FFB612] font-black tracking-widest text-sm uppercase mb-2">NBC Boxing Club</p>
          <h1 className="text-white text-2xl font-black">Rate {trainerName || "Your Trainer"}</h1>
          <p className="text-white/40 text-sm mt-1">Your review is completely anonymous</p>
        </div>

        {/* Star picker */}
        <div className="flex justify-center gap-3">
          {[1, 2, 3, 4, 5].map(s => (
            <button key={s}
              onMouseEnter={() => setHovered(s)}
              onMouseLeave={() => setHovered(0)}
              onClick={() => setRating(s)}
              data-testid={`star-${s}`}
              className="transition-transform active:scale-110"
            >
              <Star className={`w-10 h-10 ${(hovered || rating) >= s ? "fill-[#FFB612] text-[#FFB612]" : "text-white/20"} transition-colors`} />
            </button>
          ))}
        </div>
        {rating > 0 && (
          <p className="text-center text-white/50 text-sm">
            {["", "Poor", "Fair", "Good", "Great", "Outstanding!"][rating]}
          </p>
        )}

        {/* Comment */}
        <div>
          <label className="text-white/40 text-xs uppercase tracking-widest block mb-1">Comment (optional)</label>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="What did you like about the class?"
            rows={3}
            className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 rounded text-sm resize-none placeholder-white/20 focus:outline-none focus:border-[#FFB612]"
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading || !rating}
          data-testid="submit-review"
          className="w-full bg-[#FFB612] text-black font-black py-3 rounded hover:bg-[#E5A00D] transition-colors disabled:opacity-40"
        >
          {loading ? "Submitting…" : "Submit Anonymous Review"}
        </button>
      </div>
    </div>
  );
}
